import { useContext, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ToastContext, useFetch } from '@reveni-io/reveni-ui'

import { viewTypes } from '@/common/components/FilterViews/constants'
import useCreateViewDrawer from '@/common/components/FilterViews/hooks/useCreateViewDrawer/useCreateViewDrawer'
import useManageVisibilityDrawer from '@/common/components/FilterViews/hooks/useManageVisibilityDrawer/useManageVisibilityDrawer'
import useViewActions from '@/common/components/FilterViews/hooks/useViewActions/useViewActions'
import useViews from '@/common/components/FilterViews/hooks/useViews/useViews'
import { noPaginationParams, userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useCarriers from '@/common/hooks/useCarriers/useCarriers'
import useFetchPolicyRules from '@/common/hooks/useFetchPolicyRules/useFetchPolicyRules'
import useFilters from '@/common/hooks/useFilters/useFilters'
import useRegions from '@/common/hooks/useRegions/useRegions'
import useStoreProductCategories from '@/common/hooks/useStoreProductCategories/useStoreProductCategories'
import useStoreProductCollections from '@/common/hooks/useStoreProductCollections/useStoreProductCollections'
import useStoreProductTypes from '@/common/hooks/useStoreProductTypes/useStoreProductTypes'
import useStores from '@/common/hooks/useStores/useStores'
import commonServices from '@/common/services/services'
import configurationServices from '@/configuration/services/services'
import useChangeStatus from '@/returns/hooks/useChangeStatus/useChangeStatus'
import { defaultViews, filterNames, filters } from '@/returns/pages/TransactionsPage/constants'
import returnsServices from '@/returns/services/services'
import routes from '@/Router/routes.constants'

import useAlertTypes from '../useAlertTypes/useAlertTypes'
import useBulkOnHold from '../useBulkOnHold/useBulkOnHold'
import useImportCreateRmas from '../useImportCreateRmas/useImportCreateRmas'
import useImportProductValidation from '../useImportProductValidation/useImportProductValidation'
import useNewRMADrawer from '../useNewRMADrawer/useNewRMADrawer'
import useSelectedOrders from '../useSelectedOrders/useSelectedOrders'

const useTransactionsPage = ({ isUnifiedDashboard }) => {
  const { data, loading, fetchData: getReturns } = useFetch(returnsServices.getReturns, { doInitialRequest: false })
  const [showAcceptMultipleRefundModal, setShowAcceptMultipleRefundModal] = useState(false)
  const [showRejectMultipleRefundModal, setShowRejectMultipleRefundModal] = useState(false)
  const { setToastInfo } = useContext(ToastContext)
  const { info, updateContext } = useContext(AppContext)
  const storeId = isUnifiedDashboard ? null : info.selectedStore.id
  const merchantId = isUnifiedDashboard ? info.id : null
  const returnPortalId = isUnifiedDashboard ? null : info.selectedReturnPortal?.id
  const { carriers } = useCarriers({
    storeId,
    doInitialRequest: true,
  })
  const { data: logisticsData } = useFetch(
    () =>
      configurationServices.getStoreLogisticsPreferences({
        storeId,
        withUsed: true,
      }),
    { doInitialRequest: true }
  )
  const usedDeliveryMethods = logisticsData?.delivery_methods || []
  const { getStoreNameById } = useStores()
  const { data: metrics, fetchData: getMetrics } = useFetch(commonServices.getMetrics, {
    doInitialRequest: false,
  })
  const { fetchData: changeMultipleReturnStatus, loading: changingMultipleReturnStatus } = useFetch(
    returnsServices.changeReturnsStatus,
    { doInitialRequest: false }
  )
  const {
    views,
    fetchingViews,
    selectedView,
    setSelectedView,
    deleteView,
    hasViewChanges,
    setHasViewChanges,
    saveView,
    savingView,
    createView,
    creatingView,
    canEditViews,
    canManageSelectedView,
    canViewDefaultViews,
    canShareViewsWithAllGroups,
    hasAvailableViews,
  } = useViews({
    storeId,
    merchantId,
    defaultViews,
    type: isUnifiedDashboard ? viewTypes.generalTransactions : viewTypes.transactions,
    editPermission: userPermissions.editTransactionsViews,
    defaultViewsPermission: userPermissions.viewTransactionsDefaultViews,
    sharePermission: userPermissions.shareTransactionsViews,
  })
  const {
    updateFilter,
    onChangeView,
    refundDateStart,
    refundDateEnd,
    refundDatesDaysGte,
    refundDatesDaysLte,
    status,
    query,
    orderBy,
    refetch,
    transactionType,
    getFilters,
    deliveryMethodType,
    deliveryMethod,
    refetchWithoutFilters,
    lastUpdate,
    lastUpdatedGte,
    lastUpdatedLte,
    expiresIn,
    refundMethod,
    returnReason,
    rejectReason,
    forReviewReason,
    region,
    instant,
    exchangeForOrderStatus,
    paymentStatus,
    refundStatus,
    variantsSkus,
    shippingLabelStatus,
    exchangeCreatedGte,
    exchangeCreatedLte,
    exchangeCreatedDaysGte,
    exchangeCreatedDaysLte,
    hasAlerts,
    pendingAlertTypes,
    pendingAlertTypesMatch,
    returnsPageSize,
    exchangedType,
    refundCreatedGte,
    refundCreatedLte,
    refundCreatedDaysGte,
    refundCreatedDaysLte,
    productCategory,
    productCollection,
    productType,
    orderType,
    isGift,
    carrier,
    returnReasonType,
    transactionTags,
    transactionTagsMatch,
    policyRules,
    policyRulesMatch,
    hasPolicyRules,
    refundTotalAmount,
    refundTotalAmountGt,
    refundTotalAmountLt,
    refundTotalAmountGte,
    refundTotalAmountLte,
    paymentTotalAmount,
    paymentTotalAmountGt,
    paymentTotalAmountLt,
    paymentTotalAmountGte,
    paymentTotalAmountLte,
  } = useFilters({
    filters,
    doInitialRequest: true,
    fetchData: params => getReturns({ returnPortalId, params }),
    filterValues: info?.filters || selectedView?.filters,
    prevCursor: data?.previous,
    nextCursor: data?.next,
  })
  const { handleChangeView, handleSaveView, handleDiscardViewChanges } = useViewActions({
    setSelectedView,
    onChangeView,
    saveView,
    createView,
    getFilters,
    setHasViewChanges,
    selectedView,
  })
  const createViewDrawer = useCreateViewDrawer({
    storeId,
    merchantId,
    selectedView,
    getFilters,
    createView,
    canShareViewsWithAllGroups,
  })
  const manageVisibilityDrawer = useManageVisibilityDrawer({
    storeId,
    merchantId,
    selectedView,
    saveView,
    canShareViewsWithAllGroups,
  })
  const navigation = useNavigate()
  const [showExportModal, setShowExportModal] = useState(false)
  const { fetchData: exportResults, loading: exportingResults } = useFetch(returnsServices.exportResults, {
    doInitialRequest: false,
  })
  const { t } = useTranslation()
  const [onboardingDismissed, setOnboardingDismissed] = useState(() =>
    Boolean(JSON.parse(localStorage.getItem('onboardingModalDismissed')))
  )
  const { loading: fetchingReturnReasons, data: returnReasons } = useFetch(
    () =>
      returnsServices.getReturnReasons({
        returnPortalId,
        withDeleted: true,
      }),
    {
      doInitialRequest: true,
    }
  )
  const { regions, loading: fetchingRegions } = useRegions()
  const { alertTypes: availableAlertTypes, fetchingAlertTypes } = useAlertTypes({
    returnPortalId,
  })

  const handleChangeStatusSuccess = () => {
    setToastInfo({ show: true, message: t('Estado actualizado correctamente'), type: 'success' })
    refetch()
  }

  const changeStatus = useChangeStatus({ onChangeSuccess: handleChangeStatusSuccess })

  const {
    selectedOrders,
    setSelectedOrders,
    usableOrders,
    notUsableOrders,
    getSelectedOrdersAllowIssueRefund,
    getSelectedOrdersAllowManageRestock,
    getSelectedOrdersAllowsExchangeOrderProcessing,
    getOrdersWithRemainingAmount,
    getOrdersWithRequestedProducts,
    getSelectedOrdersIssueRefundAtStatus,
  } = useSelectedOrders({
    orders: data?.results,
  })

  const newRmaDrawer = useNewRMADrawer({
    storeId,
    setToastInfo,
    navigation,
  })

  const { productCategories } = useStoreProductCategories({
    storeId,
    doInitialRequest: true,
  })
  const { productCollections } = useStoreProductCollections({
    storeId,
    doInitialRequest: true,
  })
  const { productTypes } = useStoreProductTypes({
    storeId,
    doInitialRequest: true,
  })
  const { policyRulesData } = useFetchPolicyRules({
    doInitialRequest: true,
    params: {
      storeId,
      withDeleted: true,
      ...noPaginationParams,
    },
  })

  const bulkOnHold = useBulkOnHold({
    selectedTransactionIds: selectedOrders,
    setToastInfo,
    refetchTransactions: refetch,
    returnPortalId,
  })

  const importProductValidation = useImportProductValidation({
    storeId,
    setToastInfo,
  })

  const importCreateRmas = useImportCreateRmas({
    storeId,
    setToastInfo,
  })

  useEffect(() => {
    if (data) {
      setSelectedOrders([])
      updateContext(context => ({ ...context, transactions: { ...data } }))
    }
  }, [data, setSelectedOrders, updateContext])

  const refetchMetricsWhenChangeStore = () => {
    getMetrics({ returnPortalId })
  }

  useEffect(refetchMetricsWhenChangeStore, [getMetrics, returnPortalId])

  const onChangeFilter = (type, data, doRequest) => {
    if (![filterNames.orderBy, filterNames.returnsCursor].includes(type)) {
      updateFilter(filterNames.returnsCursor, '', false)
    }

    if (![filterNames.query, filterNames.returnsCursor].includes(type)) setHasViewChanges(true)
    updateFilter(type, data, doRequest)
  }

  const getReturnLink = transactionId => {
    if (isUnifiedDashboard) {
      return routes.merchantTransactionsTransactionDetailPage.replace(':id', transactionId)
    }

    return routes.transactionDetailPage.replace(':id', transactionId)
  }

  const onExportResults = ({ type, format, email }) => {
    const filters = getFilters()

    exportResults(
      { type, format, email, filters, returnPortalId },
      () => {
        setShowExportModal(false)
        setToastInfo({
          show: true,
          message: (
            <Trans i18nKey="exportSuccessMessage" values={{ email }}>
              Tu exportación se entregará por email a {email}. Esto podría tardar unos instantes dependiendo de cuántas
              devoluciones estés exportando.
            </Trans>
          ),
          type: 'success',
          ms: 5000,
        })
      },
      err => {
        setToastInfo({ show: true, message: err.errorMessage, type: 'error' })
      }
    )
  }

  const isConfigurationIncomplete = !info?.has_delivery_methods || !info?.selectedReturnPortal?.theme?.logo_url
  const showCompleteConfigurationModal = isConfigurationIncomplete && !onboardingDismissed

  const onCloseConfigurationModal = () => {
    localStorage.setItem('onboardingModalDismissed', JSON.stringify(true))
    setOnboardingDismissed(true)
  }

  const onCheckOrder = (e, id) => {
    if (e.target.checked) {
      setSelectedOrders([...selectedOrders, id])
    } else {
      setSelectedOrders(selectedOrders.filter(order => order !== id))
    }
  }

  const onCheckAllOrder = e => {
    if (e.target.checked) {
      setSelectedOrders(data?.results?.filter(order => order?.available_status?.length).map(order => order.id))
    } else {
      setSelectedOrders([])
    }
  }

  const onChangeMultipleRefunds = async ({
    issueRefund,
    restock,
    restockLocation,
    issueExchangeOrderProcessing,
    status,
    reason,
    notes,
  }) => {
    const params = {
      ids: usableOrders.map(order => order.id),
      status,
      issue_refund: issueRefund,
      restock,
      restock_location_id: restockLocation,
      issue_exchange_order_processing: issueExchangeOrderProcessing,
      reject_reason: reason,
      reject_reason_text: notes,
    }

    try {
      await changeMultipleReturnStatus({ params, returnPortalId: info?.selectedReturnPortal?.id })
      setToastInfo({ show: true, message: t('Transacciones modificadas correctamente'), type: 'success' })
      setShowAcceptMultipleRefundModal(false)
      setShowRejectMultipleRefundModal(false)
      refetch()
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const handleClickInStat = stat => {
    handleChangeView(defaultViews.find(view => view.id === stat))
  }

  const filterProps = {
    refundDates: { startDate: refundDateStart, endDate: refundDateEnd },
    refundDatesDaysGte,
    refundDatesDaysLte,
    status,
    transactionType,
    deliveryMethodType,
    deliveryMethod,
    lastUpdate,
    lastUpdatedGte,
    lastUpdatedLte,
    expiresIn,
    refundMethod,
    returnReason,
    returnReasonType,
    rejectReason,
    forReviewReason,
    region,
    instant,
    exchangeForOrderStatus,
    paymentStatus,
    refundStatus,
    variantsSkus,
    shippingLabelStatus,
    exchangeCreatedDates: { startDate: exchangeCreatedGte, endDate: exchangeCreatedLte },
    exchangeCreatedDaysGte,
    exchangeCreatedDaysLte,
    hasAlerts,
    pendingAlertTypes,
    pendingAlertTypesMatch,
    exchangedType,
    refundCreatedDates: { startDate: refundCreatedGte, endDate: refundCreatedLte },
    refundCreatedDaysGte,
    refundCreatedDaysLte,
    productCategory,
    productCollection,
    productType,
    orderType,
    isGift,
    carrier,
    transactionTags,
    transactionTagsMatch,
    policyRules,
    policyRulesMatch,
    hasPolicyRules,
    refundTotalAmount,
    refundTotalAmountGt,
    refundTotalAmountLt,
    refundTotalAmountGte,
    refundTotalAmountLte,
    paymentTotalAmount,
    paymentTotalAmountGt,
    paymentTotalAmountLt,
    paymentTotalAmountGte,
    paymentTotalAmountLte,
  }

  return {
    data,
    loading,
    info,
    metrics,

    viewState: {
      views,
      fetchingViews,
      selectedView,
      hasViewChanges,
      handleChangeView,
      handleSaveView,
      handleDiscardViewChanges,
      deleteView,
      creatingView,
      savingView,
      canEditViews,
      canManageSelectedView,
      canViewDefaultViews,
      canShareViewsWithAllGroups,
      hasAvailableViews,
    },

    createViewDrawer,
    manageVisibilityDrawer,

    filterState: {
      filterProps,
      query,
      orderBy,
      returnsPageSize,
      onChangeFilter,
      refetch,
      refetchWithoutFilters,
    },

    referenceData: {
      carriers,
      getStoreNameById,
      returnReasons,
      fetchingReturnReasons,
      regions,
      fetchingRegions,
      productCategories,
      productCollections,
      productTypes,
      usedDeliveryMethods,
      availableAlertTypes,
      fetchingAlertTypes,
      policyRules: policyRulesData?.results,
    },

    selection: {
      selectedOrders,
      onCheckOrder,
      onCheckAllOrder,
      notUsableOrders,
      getSelectedOrdersAllowIssueRefund,
      getSelectedOrdersAllowManageRestock,
      getSelectedOrdersAllowsExchangeOrderProcessing,
      getOrdersWithRemainingAmount,
      getOrdersWithRequestedProducts,
      getSelectedOrdersIssueRefundAtStatus,
      onChangeMultipleRefunds,
      changingMultipleReturnStatus,
      showAcceptMultipleRefundModal,
      setShowAcceptMultipleRefundModal,
      showRejectMultipleRefundModal,
      setShowRejectMultipleRefundModal,
    },

    exportState: {
      onExportResults,
      exportingResults,
      showExportModal,
      setShowExportModal,
    },

    changeStatus,
    newRmaDrawer,
    bulkOnHold,
    importProductValidation,
    importCreateRmas,

    onboarding: {
      showCompleteConfigurationModal,
      onCloseConfigurationModal,
    },

    getReturnLink,
    handleClickInStat,
  }
}

export default useTransactionsPage
